import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  startPhoneVerification,
  formatBirthDate,
  processAuthResult,
  type VerificationResult,
  type AuthRequestData
} from '../services/campusPhoneAuth';

interface CampusPhoneVerificationProps {
  name: string;          // 회원가입 시 입력한 이름
  birthYear: string;     // 연도
  birthMonth: string;    // 월
  birthDay: string;      // 일
  onVerificationComplete: (result: VerificationResult) => void;
  disabled?: boolean;
}

const CampusPhoneVerification: React.FC<CampusPhoneVerificationProps> = ({ 
  name,
  birthYear,
  birthMonth,
  birthDay,
  onVerificationComplete, 
  disabled = false 
}) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [personId, setPersonId] = useState<number | null>(null);
  const popupRef = useRef<Window | null>(null);

  // 상태 변경 감지를 위한 useEffect 추가
  useEffect(() => {
    console.log('CampusPhoneVerification 상태 변경 감지:', status);
  }, [status]);

  // Mobile-OK 본인인증 완료 후 결과 처리를 위한 전역 함수 등록
  useEffect(() => {
    // Mobile-OK에서 사용하는 다양한 콜백 함수들
    const handleMobileOKResult = (result: any) => {
      console.log('=== Mobile-OK 인증 결과 수신 ===');
      console.log('Mobile-OK 인증 결과 원본:', result);
      console.log('현재 상태:', status);
      
      let parsedResult;
      
      try {
        // 문자열인 경우 JSON 파싱
        if (typeof result === 'string') {
          parsedResult = JSON.parse(result);
        } else {
          parsedResult = result;
        }
        
        console.log('파싱된 Mobile-OK 결과:', parsedResult);
        
        // Mobile-OK에서 온 결과를 직접 전달
        if (parsedResult.code === 200 && parsedResult.data) {
          console.log('인증 성공 - 상태 변경 시작');
          console.log('현재 상태:', status);
          
          // 상태 변경 시도
          console.log('setStatus("success") 호출');
          setStatus('success');
          
          // 강제로 한 번 더 상태 변경 시도
          setTimeout(() => {
            console.log('강제 상태 재설정 시도');
            setStatus('success');
            console.log('상태 변경 후 확인 - 현재 상태:', status);
          }, 100);
          
          console.log('본인인증 성공:', parsedResult.data);
        } else {
          console.log('인증 실패 - 상태 변경 시작');
          setStatus('error');
          setErrorMessage(parsedResult.message || '인증에 실패했습니다.');
          console.error('본인인증 실패:', parsedResult.message);
        }
        
        console.log('부모 컴포넌트에 결과 전달 시작');
        // 부모 컴포넌트에 Mobile-OK 원본 결과 전달
        onVerificationComplete(parsedResult);
        console.log('부모 컴포넌트에 결과 전달 완료');
        
      } catch (error) {
        console.error('Mobile-OK 결과 파싱 에러:', error);
        console.log('Mobile-OK 원본 결과:', result);
        
        const errorResult = {
          code: 400,
          status: 'ERROR',
          message: '인증 결과 처리 중 오류가 발생했습니다.',
          data: { name: '', birth: '', phone: '' }
        };
        
        setStatus('error');
        setErrorMessage(errorResult.message);
        onVerificationComplete(errorResult as any); // Mobile-OK 결과 형식으로 전달
      }
      
      console.log('=== Mobile-OK 결과 처리 완료 ===');
    };
    
    // 다양한 Mobile-OK 콜백 함수명들 등록
    const callbackNames = [
      'mobileOKResult',
      'mobileOKCallback', 
      'mobileAuthResult',
      'receiveAuthResult',
      'niceAuthResult',
      'checkplusResult',
      'mobileOKAuthResult',
      'authResult',
      'mobileOKComplete',
      'authComplete',
      'mobileAuthCallback',
      'mobileOK',
      'authCallback',
      'verificationResult',
      'verificationComplete'
    ];
    
    callbackNames.forEach(name => {
      (window as any)[name] = handleMobileOKResult;
      // console.log(`Mobile-OK 콜백 등록: ${name}`);
    });
    
    // 테스트용 전역 함수도 등록
    (window as any).testMobileOK = () => {
      console.log('테스트용 Mobile-OK 호출');
      handleMobileOKResult({
        "code": 200,
        "status": "OK", 
        "message": "요청 성공 [인증 성공]",
        "data": {
          "name": "박혜림",
          "birth": "2001-12-22",
          "phone": "01022882418"
        }
      });
    };
    
    console.log('모든 Mobile-OK 콜백 함수 등록 완료');
    console.log('테스트: window.testMobileOK() 실행 가능');
    
    // 기존 NICE 콜백도 유지 (하위 호환성)
    (window as any).niceAuthCallback = async (encryptMOKKeyToken: string) => {
      console.log('NICE 본인인증 완료, 결과 처리 시작:', encryptMOKKeyToken);
      
      try {
        const expectedBirth = formatBirthDate(birthYear, birthMonth, birthDay);
        const result = await processAuthResult(encryptMOKKeyToken, name, expectedBirth);
        
        if (result.success && result.userData) {
          setPersonId(result.userData.id);
          setStatus('success');
          console.log('본인인증 성공:', result.userData);
        } else {
          setStatus('error');
          setErrorMessage(result.message);
          console.error('본인인증 실패:', result.message);
        }
        
        onVerificationComplete(result);
        
      } catch (error: any) {
        console.error('인증 결과 처리 중 오류:', error);
        const errorResult: VerificationResult = {
          success: false,
          message: error.message || '인증 결과 처리 중 오류가 발생했습니다.'
        };
        
        setStatus('error');
        setErrorMessage(errorResult.message);
        onVerificationComplete(errorResult);
      }
    };

    // cleanup
    return () => {
      delete (window as any).mobileOKResult;
      delete (window as any).mobileOKCallback;
      delete (window as any).mobileAuthResult;
      delete (window as any).receiveAuthResult;
      delete (window as any).niceAuthResult;
      delete (window as any).checkplusResult;
      delete (window as any).niceAuthCallback;
    };
  }, [name, birthYear, birthMonth, birthDay, onVerificationComplete]);

  // 본인확인 시작
  const startVerification = useCallback(async (e?: React.MouseEvent) => {
    // 이벤트 방지
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('startVerification 시작 - 폼 제출 방지됨');
    setStatus('loading');
    setErrorMessage('');

    try {
      const authData: AuthRequestData = {
        name,
        birthYear,
        birthMonth,
        birthDay
      };

      // 본인인증 팝업 열기
      const popup = await startPhoneVerification(authData);
      
      if (popup) {
        popupRef.current = popup;
        console.log('NICE 본인인증 창이 열렸습니다. 인증을 완료해주세요.');
      } else {
        throw new Error('본인인증 창을 열 수 없습니다.');
      }
      
    } catch (error: any) {
      console.error('본인확인 실패:', error);
      setErrorMessage(error.message || '본인확인에 실패했습니다.');
      setStatus('error');
      
      onVerificationComplete({
        success: false,
        message: error.message || '본인확인에 실패했습니다.'
      });
    }
  }, [name, birthYear, birthMonth, birthDay, onVerificationComplete]);

  // 다시 시도
  const handleRetry = useCallback(() => {
    console.log('인증 다시 시도 - 상태 초기화');
    
    // 상태 초기화
    setStatus('idle');
    setErrorMessage('');
    setPersonId(null);
    
    // 팝업 참조 초기화
    popupRef.current = null;
  }, []);

  // 버튼 렌더링
  const renderButton = () => {
    if (status === 'success') {
      return (
        <VerificationButton type="button" disabled className="success" style={{
          backgroundColor: 'var(--Interaction-Disable, #F4F4F5)',
          color: 'var(--Label-Assistive, rgba(55, 56, 60, 0.28))'
        }}>
          휴대폰 인증
        </VerificationButton>
      );
    }

    return (
      <VerificationButton
        type="button"
        onClick={startVerification}
        disabled={false}
      >
        휴대폰 인증
      </VerificationButton>
    );
  };

  return (
    <Container>
      {renderButton()}
      
      {status === 'success' && (
        <div style={{ 
          color: 'var(--Status-Positive, #00BF40)', 
          fontSize: '12px', 
          textAlign: 'left',
          marginTop: '8px',
          fontFamily: 'Pretendard JP',
          fontWeight: '400',
          lineHeight: '16.01px',
          letterSpacing: '0.30px'
        }}>
          인증되었습니다.
        </div>
      )}
      
      {status === 'error' && (
        <div style={{ 
          color: 'var(--Status-Destructive, #FF3B30)', 
          fontSize: '12px', 
          textAlign: 'left',
          marginTop: '8px',
          fontFamily: 'Pretendard JP',
          fontWeight: '400',
          lineHeight: '16.01px',
          letterSpacing: '0.30px'
        }}>
          {errorMessage || '인증에 실패했습니다.'}
        </div>
      )}
    </Container>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
`;

const VerificationButton = styled.button`
  height: 52px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: #448181;
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  padding: 16px 28px;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #3a6e6e;
  }
  
  &:disabled {
    background-color: #F4F4F5;
    color: rgba(55, 56, 60, 0.28);
    cursor: not-allowed;
  }
  
  &.success {
    background-color: #00BF40;
    color: white;
  }
`;

const StatusMessage = styled.div`
  font-size: 12px;
  line-height: 1.334em;
  margin-top: 8px;
  
  &.loading {
    color: #448181;
  }
  
  &.success {
    color: #00BF40;
  }
  
  &.error {
    color: #FF4242;
  }
  
  &.info {
    color: rgba(55, 56, 60, 0.61);
  }
`;

const ErrorContainer = styled.div`
  margin-top: 8px;
`;

const RetryButton = styled.button`
  background: none;
  border: none;
  color: #448181;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  margin-top: 4px;
  padding: 0;
  
  &:hover {
    color: #3a6e6e;
  }
`;

export default CampusPhoneVerification;
